import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { DIRECTIONS, Directions } from '../../../../models/positions.types';
import { PROGRESS_TRACKER_STEP_APPEARANCES, ProgressTrackerStepAppearance } from './models/progress-tracker-step.types';

@Component({
  tag: 'wink-progress-tracker-step',
  styleUrl: 'styles/progress-tracker-step.default.scss',
  shadow: true,
})
export class ProgressTrackerStep {
  @Element() host!: HTMLWinkProgressTrackerStepElement;

  @Prop() appearance: ProgressTrackerStepAppearance = PROGRESS_TRACKER_STEP_APPEARANCES.default;
  @Prop() direction?: Directions;
  @Prop() alignment?: Directions;
  @Prop() isActive = false;
  @Prop() isCompleted = false;
  @Prop() counter: number;
  @Prop() icon?: string;
  @Prop() isLast = false;

  @State() status = 'default';

  /**
   * Fallback: temporarily supports both the new `direction` prop and the legacy `alignment` prop.
   * Prefer using `direction`. `alignment` is deprecated and will be removed in a future release.
   */
  private getDirection(): Directions {
    if (this.direction !== undefined) return this.direction;
    if (this.alignment !== undefined) {
      console.warn('[wink-progress-tracker-step] "alignment" is deprecated, use "direction" instead.');
      return this.alignment;
    }
    return DIRECTIONS.vertical;
  }

  private getClass(): { [key: string]: boolean } {
    return {
      [`progress-tracker-step status--${this.getStatus()} direction--${this.getDirection()}`]: true,
      [`is-last`]: this.isLast,
    };
  }

  private getStatus() {
    if (this.isCompleted) {
      return 'completed';
    }
    if (this.isActive) {
      return 'active';
    }
    return 'default';
  }

  private isCounterTicked(): boolean {
    return this.getStatus() === 'completed' && this.appearance === PROGRESS_TRACKER_STEP_APPEARANCES.ticked;
  }

  private getExportParts(): string {
    if (this.isCounterTicked()) {
      return 'icon, icon-tick';
    }
    if (this.icon) {
      return `icon, icon-${this.icon}`;
    }
    return '';
  }

  private getCounterOrIcon() {
    if (this.isCounterTicked()) {
      return <wink-icon appearance="default" icon="tick" size="sm"></wink-icon>;
    }
    if (this.counter) return this.counter;
    return <wink-icon appearance="default" icon={this.icon} size="sm"></wink-icon>;
  }

  render() {
    return (
      <Host exportparts={this.getExportParts()} role="listitem">
        <div class={this.getClass()}>
          <div class="counter" part="counter backdrop backdrop-border-mask-before">
            {this.getCounterOrIcon()}
          </div>
          <div class="content">{this.getDirection() === DIRECTIONS.vertical && <slot name="content"></slot>}</div>
        </div>
      </Host>
    );
  }
}
